import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  QrCode,
  Smartphone,
  Banknote,
  Copy,
  Check,
  Download,
  Loader2,
  CheckCircle2,
  Clock,
  User,
  FileText,
  RefreshCw,
  Phone,
  AlertCircle,
  ArrowRight,
  RotateCcw,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const POSCheckout = () => {
  const { isDarkMode } = useOutletContext() || {};
  const amountInputRef = useRef(null);
  
  // Form state
  const [step, setStep] = useState('form'); // form, payment, success
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'LRD',
    description: '',
    customer_name: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  
  // Transaction state
  const [transaction, setTransaction] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [successCountdown, setSuccessCountdown] = useState(3);

  // Focus amount input on mount
  useEffect(() => {
    if (amountInputRef.current && step === 'form') {
      amountInputRef.current.focus();
    }
  }, [step]);

  // Poll for transaction status updates (for QR and Mobile Money)
  useEffect(() => {
    let interval;
    if (transaction && 
        (transaction.payment_method === 'qr' || transaction.payment_method === 'mobile_money') &&
        transaction.status === 'pending' &&
        step === 'payment') {
      interval = setInterval(async () => {
        setCheckingStatus(true);
        try {
          const res = await fetch(`${API_URL}/api/merchant/transactions/${transaction.transaction_id}`, {
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            if (data.status === 'confirmed') {
              setTransaction(prev => ({ ...prev, status: 'confirmed', confirmed_at: new Date().toISOString() }));
              setStep('success');
              toast.success('Payment confirmed!');
              clearInterval(interval);
            }
          }
        } catch (error) {
          console.error('Error checking status:', error);
        } finally {
          setCheckingStatus(false);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [transaction, step]);

  // Auto-return countdown after success
  useEffect(() => {
    let countdown;
    if (step === 'success') {
      setSuccessCountdown(3);
      countdown = setInterval(() => {
        setSuccessCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdown);
            resetForm();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(countdown);
  }, [step]);

  const handlePayment = async (method) => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      amountInputRef.current?.focus();
      return;
    }

    setLoading(true);
    try {
      const payload = {
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        description: formData.description || `POS Sale - ${new Date().toLocaleTimeString()}`,
        customer_name: formData.customer_name || null,
        notes: formData.notes || null,
        payment_method: method
      };

      if (method === 'mobile_money') {
        payload.network = 'MTN';
      }

      const res = await fetch(`${API_URL}/api/merchant/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        setTransaction(data);
        setStep('payment');
        toast.success('Transaction created');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create transaction');
      }
    } catch (error) {
      toast.error('Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCash = async () => {
    if (!transaction) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/merchant/checkout/${transaction.transaction_id}/confirm-cash`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setTransaction(prev => ({ 
          ...prev, 
          status: 'confirmed',
          confirmed_at: data.confirmed_at,
          confirmed_by: data.confirmed_by
        }));
        setStep('success');
        toast.success('Payment confirmed!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to confirm');
      }
    } catch (error) {
      toast.error('Failed to confirm payment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!transaction) {
      resetForm();
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/merchant/checkout/${transaction.transaction_id}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.info('Transaction cancelled');
    } catch (error) {
      // Ignore errors
    }
    resetForm();
  };

  const resetForm = () => {
    setStep('form');
    setFormData({ amount: '', currency: 'LRD', description: '', customer_name: '', notes: '' });
    setTransaction(null);
    setSuccessCountdown(3);
    setTimeout(() => amountInputRef.current?.focus(), 100);
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const downloadQRCode = () => {
    if (!transaction?.qr_code) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${transaction.qr_code}`;
    link.download = `POS-${transaction.reference}.png`;
    link.click();
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (step === 'form' && !loading) {
        if (e.key === 'F1') {
          e.preventDefault();
          handlePayment('qr');
        } else if (e.key === 'F2') {
          e.preventDefault();
          handlePayment('mobile_money');
        } else if (e.key === 'F3') {
          e.preventDefault();
          handlePayment('cash');
        }
      } else if (step === 'payment' && transaction?.payment_method === 'cash' && e.key === 'Enter') {
        e.preventDefault();
        handleConfirmCash();
      } else if (step === 'success' && e.key === 'Enter') {
        e.preventDefault();
        resetForm();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (step !== 'form') {
          handleCancel();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, loading, transaction]);

  const formatCurrency = (amount, currency) => {
    return `${currency} ${parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="h-full flex flex-col" data-testid="pos-checkout">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            POS Checkout
          </h1>
          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            F1: QR • F2: Mobile • F3: Cash • ESC: Cancel
          </p>
        </div>
        {step !== 'form' && (
          <button
            onClick={handleCancel}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
              isDarkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
            }`}
          >
            <RotateCcw className="w-4 h-4" />
            New Sale
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 overflow-hidden">
        
        {/* Left Panel - Sale Info */}
        <div className={`w-1/2 rounded-xl p-5 flex flex-col ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          
          {step === 'form' && (
            <>
              {/* Amount Display */}
              <div className="flex-1 flex flex-col justify-center">
                <div className="text-center mb-6">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    AMOUNT
                  </label>
                  <div className="flex items-center justify-center gap-3 mt-2">
                    <input
                      ref={amountInputRef}
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      className={`text-5xl font-bold text-center w-64 bg-transparent border-none outline-none ${
                        isDarkMode ? 'text-white placeholder-slate-600' : 'text-slate-800 placeholder-slate-300'
                      }`}
                      data-testid="pos-amount-input"
                    />
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className={`text-2xl font-bold bg-transparent border-none outline-none cursor-pointer ${
                        isDarkMode ? 'text-teal-400' : 'text-teal-600'
                      }`}
                    >
                      <option value="LRD">LRD</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  {formData.amount && (
                    <p className={`mt-2 text-lg ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {formatCurrency(formData.amount, formData.currency)}
                    </p>
                  )}
                </div>

                {/* Quick Fields */}
                <div className="space-y-3 max-w-md mx-auto w-full">
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description (optional)"
                    className={`w-full px-4 py-2.5 rounded-lg border text-sm ${
                      isDarkMode
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                        : 'bg-gray-50 border-gray-200 text-slate-800 placeholder-slate-400'
                    }`}
                  />
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      placeholder="Customer name"
                      className={`flex-1 px-4 py-2.5 rounded-lg border text-sm ${
                        isDarkMode
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                          : 'bg-gray-50 border-gray-200 text-slate-800 placeholder-slate-400'
                      }`}
                    />
                    <input
                      type="text"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Notes"
                      className={`flex-1 px-4 py-2.5 rounded-lg border text-sm ${
                        isDarkMode
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                          : 'bg-gray-50 border-gray-200 text-slate-800 placeholder-slate-400'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 'payment' && transaction && (
            <div className="flex-1 flex flex-col items-center justify-center">
              {/* Amount Summary */}
              <div className={`text-center mb-6 p-6 rounded-2xl w-full ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Amount Due</p>
                <p className={`text-4xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                  {formatCurrency(transaction.amount, transaction.currency)}
                </p>
                {formData.customer_name && (
                  <p className={`mt-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Customer: {formData.customer_name}
                  </p>
                )}
              </div>

              {/* Reference */}
              <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>REF:</span>
                <span className={`font-mono font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                  {transaction.reference}
                </span>
                <button
                  onClick={() => copyToClipboard(transaction.reference, 'ref')}
                  className="p-1 rounded hover:bg-black/10"
                >
                  {copied === 'ref' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                </button>
              </div>

              {/* Status Indicator */}
              <div className="mt-6 flex items-center gap-2">
                {transaction.status === 'pending' && (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
                    <span className="text-yellow-500 font-medium">Waiting for payment...</span>
                  </>
                )}
                {transaction.status === 'awaiting_confirmation' && (
                  <>
                    <Clock className="w-5 h-5 text-blue-500" />
                    <span className="text-blue-500 font-medium">Ready for confirmation</span>
                  </>
                )}
              </div>
            </div>
          )}

          {step === 'success' && transaction && (
            <div className="flex-1 flex flex-col items-center justify-center">
              {/* Success Animation */}
              <div className="relative">
                <div className="w-28 h-28 rounded-full bg-green-100 flex items-center justify-center animate-pulse">
                  <CheckCircle2 className="w-16 h-16 text-green-500" />
                </div>
              </div>
              
              <h2 className={`text-2xl font-bold mt-6 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                Payment Received!
              </h2>
              
              <div className={`mt-6 p-6 rounded-2xl text-center w-full max-w-sm ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                <p className="text-3xl font-bold text-green-500">
                  {formatCurrency(transaction.amount, transaction.currency)}
                </p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {transaction.payment_method === 'qr' && 'QR Code Payment'}
                    {transaction.payment_method === 'mobile_money' && 'Mobile Money'}
                    {transaction.payment_method === 'cash' && 'Cash Payment'}
                  </span>
                </div>
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  {transaction.reference}
                </p>
              </div>

              <p className={`mt-6 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Returning to checkout in {successCountdown}s...
              </p>
              
              <button
                onClick={resetForm}
                className="mt-4 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 font-medium"
              >
                New Sale
              </button>
            </div>
          )}
        </div>

        {/* Right Panel - Payment Options */}
        <div className={`w-1/2 rounded-xl p-5 flex flex-col ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          
          {step === 'form' && (
            <div className="flex-1 flex flex-col">
              <h3 className={`text-sm font-medium mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                SELECT PAYMENT METHOD
              </h3>
              
              <div className="flex-1 flex flex-col gap-4">
                {/* QR Payment Button */}
                <button
                  onClick={() => handlePayment('qr')}
                  disabled={loading || !formData.amount}
                  className={`flex-1 rounded-2xl border-2 transition-all duration-200 flex flex-col items-center justify-center gap-3 ${
                    loading || !formData.amount
                      ? 'opacity-50 cursor-not-allowed border-gray-200'
                      : 'border-teal-200 hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/30 active:scale-98'
                  } ${isDarkMode ? 'border-slate-600' : ''}`}
                  data-testid="pos-qr-btn"
                >
                  <div className="w-20 h-20 rounded-2xl bg-teal-100 flex items-center justify-center">
                    <QrCode className="w-10 h-10 text-teal-600" />
                  </div>
                  <div className="text-center">
                    <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      QR Code
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Customer scans to pay
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-slate-500'}`}>
                    F1
                  </span>
                </button>

                {/* Mobile Money Button */}
                <button
                  onClick={() => handlePayment('mobile_money')}
                  disabled={loading || !formData.amount}
                  className={`flex-1 rounded-2xl border-2 transition-all duration-200 flex flex-col items-center justify-center gap-3 ${
                    loading || !formData.amount
                      ? 'opacity-50 cursor-not-allowed border-gray-200'
                      : 'border-orange-200 hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/30 active:scale-98'
                  } ${isDarkMode ? 'border-slate-600' : ''}`}
                  data-testid="pos-mobile-btn"
                >
                  <div className="w-20 h-20 rounded-2xl bg-orange-100 flex items-center justify-center">
                    <Smartphone className="w-10 h-10 text-orange-600" />
                  </div>
                  <div className="text-center">
                    <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      Mobile Money
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      MTN or Orange
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-slate-500'}`}>
                    F2
                  </span>
                </button>

                {/* Cash Button */}
                <button
                  onClick={() => handlePayment('cash')}
                  disabled={loading || !formData.amount}
                  className={`flex-1 rounded-2xl border-2 transition-all duration-200 flex flex-col items-center justify-center gap-3 ${
                    loading || !formData.amount
                      ? 'opacity-50 cursor-not-allowed border-gray-200'
                      : 'border-green-200 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/30 active:scale-98'
                  } ${isDarkMode ? 'border-slate-600' : ''}`}
                  data-testid="pos-cash-btn"
                >
                  <div className="w-20 h-20 rounded-2xl bg-green-100 flex items-center justify-center">
                    <Banknote className="w-10 h-10 text-green-600" />
                  </div>
                  <div className="text-center">
                    <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      Cash
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Manual confirmation
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-slate-500'}`}>
                    F3
                  </span>
                </button>
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Loader2 className="w-5 h-5 animate-spin text-teal-500" />
                  <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Creating transaction...</span>
                </div>
              )}
            </div>
          )}

          {step === 'payment' && transaction && (
            <div className="flex-1 flex flex-col">
              {/* QR Code Display */}
              {transaction.payment_method === 'qr' && transaction.qr_code && (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className={`p-4 rounded-2xl ${isDarkMode ? 'bg-white' : 'bg-gray-50'}`}>
                    <img
                      src={`data:image/png;base64,${transaction.qr_code}`}
                      alt="QR Code"
                      className="w-52 h-52"
                    />
                  </div>
                  
                  <button
                    onClick={downloadQRCode}
                    className={`mt-4 flex items-center gap-2 px-4 py-2 rounded-lg ${
                      isDarkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-slate-700'
                    }`}
                  >
                    <Download className="w-4 h-4" />
                    Download QR
                  </button>

                  <div className={`mt-4 p-3 rounded-lg text-center ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {checkingStatus && <RefreshCw className="w-4 h-4 inline animate-spin mr-1" />}
                      Customer scans QR to pay. Auto-confirms on receipt.
                    </p>
                  </div>
                </div>
              )}

              {/* Mobile Money Display */}
              {transaction.payment_method === 'mobile_money' && (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
                    <p className={`text-sm text-center ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                      Include reference <strong className="font-mono">{transaction.reference}</strong> in payment
                    </p>
                  </div>

                  {transaction.payment_sources?.map((source, idx) => (
                    <div
                      key={idx}
                      className={`w-full p-4 rounded-xl mb-3 ${
                        source.network === 'MTN'
                          ? 'bg-yellow-50 border border-yellow-200'
                          : 'bg-orange-50 border border-orange-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Phone className={`w-5 h-5 ${source.network === 'MTN' ? 'text-yellow-600' : 'text-orange-600'}`} />
                          <div>
                            <p className={`font-semibold ${source.network === 'MTN' ? 'text-yellow-700' : 'text-orange-700'}`}>
                              {source.network}
                            </p>
                            <p className="font-mono text-lg">{source.phone_number}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => copyToClipboard(source.phone_number, `phone-${idx}`)}
                          className="p-2 rounded-lg hover:bg-black/5"
                        >
                          {copied === `phone-${idx}` ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className={`mt-4 p-3 rounded-lg text-center ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                    <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {checkingStatus && <RefreshCw className="w-4 h-4 inline animate-spin mr-1" />}
                      Auto-confirms when SMS is received
                    </p>
                  </div>
                </div>
              )}

              {/* Cash Payment Display */}
              {transaction.payment_method === 'cash' && (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className={`p-8 rounded-2xl text-center ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                    <Banknote className="w-20 h-20 mx-auto text-green-500 mb-4" />
                    <p className={`text-xl font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                      Collect Cash
                    </p>
                    <p className={`text-3xl font-bold mt-2 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      {formatCurrency(transaction.amount, transaction.currency)}
                    </p>
                  </div>

                  <button
                    onClick={handleConfirmCash}
                    disabled={loading}
                    className="w-full mt-6 py-4 bg-green-500 text-white text-xl font-bold rounded-xl hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-3 active:scale-98 transition-transform"
                    data-testid="pos-confirm-cash-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle2 className="w-6 h-6" />
                        Confirm Cash Received
                      </>
                    )}
                  </button>
                  
                  <p className={`mt-3 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Press Enter to confirm
                  </p>
                </div>
              )}

              {/* Cancel Button */}
              <button
                onClick={handleCancel}
                className={`mt-4 py-2 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-500'} hover:underline`}
              >
                Cancel Transaction
              </button>
            </div>
          )}

          {step === 'success' && (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className={`p-6 rounded-2xl text-center ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Transaction Complete</p>
                <p className={`text-lg font-mono mt-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {transaction?.reference}
                </p>
                <p className={`text-sm mt-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  {new Date().toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default POSCheckout;
